import { useState } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d8c0pjgeb6p27cmomh40';

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const message = String(formData.get('message') || '');
    if (message.length > 500) {
      setStatus('error');
      setErrorMsg('Message must be 500 characters or fewer.');
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === 'string') params.append(key, value);
    });

    setStatus('sending');
    setErrorMsg('');
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (!res.ok) throw new Error('Network error');
      setStatus('success');
      form.reset();
    } catch (err) {
      setStatus('error');
      setErrorMsg('Something went wrong. Try again or email hello@vex.vc.');
    }
  };

  return (
    <form
      id="vex-start-a-chat"
      data-readdy-form
      onSubmit={handleSubmit}
      className="glass-card rounded-2xl p-6 md:p-10"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-xs font-mono uppercase tracking-[0.2em] text-white/40 mb-2">
            Your name
          </label>
          <input
            name="name"
            required
            type="text"
            className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-white/40 focus:outline-none transition-colors"
            placeholder="Ada Lovelace"
          />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-[0.2em] text-white/40 mb-2">
            Email
          </label>
          <input
            name="email"
            required
            type="email"
            className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-white/40 focus:outline-none transition-colors"
            placeholder="ada@yourstartup.com"
          />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-[0.2em] text-white/40 mb-2">
            Company
          </label>
          <input
            name="company"
            type="text"
            className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-white/40 focus:outline-none transition-colors"
            placeholder="Stealth labs"
          />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-[0.2em] text-white/40 mb-2">
            I am
          </label>
          <select
            name="role"
            required
            defaultValue=""
            className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-3 text-sm text-white focus:border-white/40 focus:outline-none transition-colors"
          >
            <option value="" disabled>Select role</option>
            <option value="Founder">A founder</option>
            <option value="Operator">An operator</option>
            <option value="Investor">A co-investor</option>
            <option value="Other">Something else</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-[0.2em] text-white/40 mb-2">
            Stage
          </label>
          <select
            name="stage"
            defaultValue=""
            className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-3 text-sm text-white focus:border-white/40 focus:outline-none transition-colors"
          >
            <option value="" disabled>Select stage</option>
            <option value="Idea">Idea / Napkin</option>
            <option value="Pre-seed">Pre-seed</option>
            <option value="Seed">Seed</option>
            <option value="Series A">Series A</option>
            <option value="Later">Later stage</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-[0.2em] text-white/40 mb-2">
            Sector
          </label>
          <select
            name="sector"
            defaultValue=""
            className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-3 text-sm text-white focus:border-white/40 focus:outline-none transition-colors"
          >
            <option value="" disabled>Select sector</option>
            <option value="AI">Artificial Intelligence</option>
            <option value="Robotics">Robotics</option>
            <option value="Bio">Bio & Life Sciences</option>
            <option value="Climate">Climate & Energy</option>
            <option value="Space">Space & Aerospace</option>
            <option value="Quantum">Quantum & Compute</option>
            <option value="Other">Other frontier</option>
          </select>
        </div>
      </div>

      <div className="mt-5">
        <label className="block text-xs font-mono uppercase tracking-[0.2em] text-white/40 mb-2">
          Tell us what you’re building <span className="text-white/30 normal-case">(max 500 chars)</span>
        </label>
        <textarea
          name="message"
          required
          maxLength={500}
          rows={5}
          className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-white/40 focus:outline-none transition-colors resize-none"
          placeholder="A short paragraph beats a long deck. What problem, why now, why you?"
        />
      </div>

      <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <p className="text-xs text-white/40 font-mono">
          We reply within 72 hours. Always.
        </p>
        <button
          type="submit"
          disabled={status === 'sending'}
          className="bg-white text-black px-7 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2 disabled:opacity-50"
        >
          {status === 'sending' ? 'Sending...' : 'Start a chat'}
          <i className="ri-arrow-right-line"></i>
        </button>
      </div>

      {status === 'success' && (
        <div className="mt-6 p-4 rounded-lg border border-emerald-400/30 bg-emerald-400/5 text-emerald-200 text-sm flex items-center gap-3">
          <i className="ri-check-line text-lg"></i>
          Got it — we’ll be in touch within 72 hours.
        </div>
      )}
      {status === 'error' && (
        <div className="mt-6 p-4 rounded-lg border border-rose-400/30 bg-rose-400/5 text-rose-200 text-sm flex items-center gap-3">
          <i className="ri-error-warning-line text-lg"></i>
          {errorMsg || 'Something went wrong. Try again.'}
        </div>
      )}
    </form>
  );
}